// ajay:code 30-07-26
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Save, Lock } from "lucide-react";
import { productAPI, pdirAPI } from "../../services/api";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PdirForm({ basePath = "/company/pdir" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEditRoute = location.pathname.endsWith("/edit"); // /pdir/:id/edit
  const isAddMode = !id;
  const isLoadExisting = Boolean(id); // view OR edit both load an existing report

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("Draft");

  const [header, setHeader] = useState({
    dwg_change_level: "",
    inv_no: "",
    no_of_cavities: "",
    qty: "",
    date: todayStr(),
    batch_ref: "",
  });
  const [overallRemark, setOverallRemark] = useState("");
  const [checkedBy, setCheckedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  const [loading, setLoading] = useState(isLoadExisting);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Final reports are read-only no matter what route was hit (defense in depth —
  // backend already rejects the edit call too, this just avoids showing enabled
  // inputs that would fail on submit).
  const isReadOnly = isLoadExisting && (!isEditRoute || status === "Final");

  // ── Add mode: load product list, then parameters when a product is picked ──
  useEffect(() => {
    if (isLoadExisting) return;
    productAPI.list().then((r) => {
      const active = (r.data || []).filter((p) => p.status === "Active");
      setProducts(active);
    });
  }, [isLoadExisting]);

  const loadParameters = useCallback((pid) => {
    if (!pid) {
      setItems([]);
      return;
    }
    pdirAPI.listParameters(pid).then((r) => {
      const activeParams = (r.data || []).filter((p) => p.status === "Active");
      setItems(
        activeParams.map((p) => ({
          parameter: p.parameter,
          instrument: p.instrument,
          observation_1: "",
          observation_2: "",
          observation_3: "",
          observation_4: "",
          observation_5: "",
          remark: "",
        })),
      );
    });
  }, []);

  useEffect(() => {
    if (isAddMode) loadParameters(productId);
  }, [productId, isAddMode, loadParameters]);

  // ── View/Edit mode: load the saved report ──
  useEffect(() => {
    if (!isLoadExisting) return;
    setLoading(true);
    pdirAPI
      .getReport(id)
      .then((r) => {
        const report = r.data;
        setStatus(report.status);
        setProductId(String(report.product_id));
        setProducts([
          {
            id: report.product_id,
            name: report.Product?.name,
            code: report.Product?.code,
          },
        ]);
        setHeader({
          dwg_change_level: report.dwg_change_level || "",
          inv_no: report.inv_no || "",
          no_of_cavities: report.no_of_cavities ?? "",
          qty: report.qty ?? "",
          date: report.date,
          batch_ref: report.batch_ref || "",
        });
        setOverallRemark(report.overall_remark || "");
        setCheckedBy(report.checked_by || "");
        setApprovedBy(report.approved_by || "");
        setItems((report.items || []).map((it) => ({ ...it })));
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [id, isLoadExisting]);

  const updateItem = (i, field, value) => {
    setItems((prev) =>
      prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)),
    );
  };

  const submit = async () => {
    setErr("");
    if (!productId) return setErr("Select a part");
    if (items.length === 0)
      return setErr("No parameters found — add some in PDIR Master first");

    setSaving(true);
    try {
      const payload = {
        ...header,
        no_of_cavities: header.no_of_cavities ? +header.no_of_cavities : null,
        qty: header.qty ? +header.qty : null,
        overall_remark: overallRemark,
        checked_by: checkedBy,
        approved_by: approvedBy,
        items,
      };
      if (isEditRoute) {
        await pdirAPI.updateReport(id, payload);
      } else {
        await pdirAPI.createReport({ product_id: productId, ...payload });
      }
      navigate(basePath);
    } catch (e) {
      setErr(e.message || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (
      !window.confirm(
        "Finalize this PDIR report? Once finalized it can NEVER be edited again, by anyone.",
      )
    )
      return;
    setSaving(true);
    try {
      await pdirAPI.finalizeReport(id);
      setStatus("Final");
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedProduct = products.find(
    (p) => String(p.id) === String(productId),
  );

  if (loading)
    return <div style={{ padding: 28, color: "var(--text2)" }}>Loading...</div>;

  return (
    <div style={{ padding: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <button onClick={() => navigate(basePath)} style={iconBtn}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            {isAddMode
              ? "Add PDIR"
              : isReadOnly
                ? "PDIR Report (View)"
                : "Edit PDIR Report"}
          </h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Pre-Despatch Inspection Report
          </p>
        </div>
        {isLoadExisting && (
          <span
            className={`badge ${status === "Final" ? "badge-success" : "badge-danger"}`}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {status === "Final" && <Lock size={11} />} {status}
          </span>
        )}
        {isLoadExisting && status === "Draft" && !isEditRoute && (
          <button
            className="btn-secondary"
            onClick={() => navigate(`${basePath}/${id}/edit`)}
          >
            Edit
          </button>
        )}
        {isLoadExisting && status === "Draft" && (
          <button
            className="btn-primary"
            onClick={finalize}
            disabled={saving}
            style={{
              background: "var(--danger)",
              borderColor: "var(--danger)",
            }}
          >
            Finalize (Lock)
          </button>
        )}
      </div>

      {err && (
        <div
          className="card"
          style={{ color: "var(--danger)", marginBottom: 16 }}
        >
          {err}
        </div>
      )}
      {isLoadExisting && status === "Final" && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            color: "var(--text2)",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Lock size={14} /> This report is finalized and permanently locked —
          it cannot be edited by anyone.
        </div>
      )}

      {/* Product + header fields */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 14,
          }}
        >
          <div className="form-group">
            <label>Part</label>
            {!isAddMode ? (
              <input
                type="text"
                disabled
                value={`${selectedProduct?.name || ""} (${selectedProduct?.code || ""})`}
              />
            ) : (
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                <option value="">— Select part —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Dwg Change Level</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.dwg_change_level}
              onChange={(e) =>
                setHeader({ ...header, dwg_change_level: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>INV No.</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.inv_no}
              onChange={(e) => setHeader({ ...header, inv_no: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>No. of Cavities</label>
            <input
              type="number"
              disabled={isReadOnly}
              value={header.no_of_cavities}
              onChange={(e) =>
                setHeader({ ...header, no_of_cavities: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>Qty</label>
            <input
              type="number"
              disabled={isReadOnly}
              value={header.qty}
              onChange={(e) => setHeader({ ...header, qty: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              disabled={isReadOnly}
              value={header.date}
              max={todayStr()}
              onChange={(e) => setHeader({ ...header, date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Batch Ref</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={header.batch_ref}
              onChange={(e) =>
                setHeader({ ...header, batch_ref: e.target.value })
              }
            />
          </div>
        </div>
      </div>

      {/* Parameter observations grid */}
      <div
        className="card"
        style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}
      >
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>S.N.</th>
                <th style={{ minWidth: 200 }}>Parameter</th>
                <th style={{ width: 120 }}>Instrument</th>
                <th style={{ width: 70 }}>Obs. 1</th>
                <th style={{ width: 70 }}>Obs. 2</th>
                <th style={{ width: 70 }}>Obs. 3</th>
                <th style={{ width: 70 }}>Obs. 4</th>
                <th style={{ width: 70 }}>Obs. 5</th>
                <th style={{ width: 90 }}>Remark</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    {isAddMode
                      ? "Select part to load its checklist"
                      : "No items"}
                  </td>
                </tr>
              )}
              {items.map((row, i) => (
                <tr key={i}>
                  <td className="text3">{i + 1}</td>
                  <td style={{ fontSize: 12 }}>{row.parameter}</td>
                  <td className="text2" style={{ fontSize: 12 }}>
                    {row.instrument}
                  </td>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <td key={n}>
                      <input
                        type="text"
                        disabled={isReadOnly}
                        value={row[`observation_${n}`] || ""}
                        onChange={(e) =>
                          updateItem(i, `observation_${n}`, e.target.value)
                        }
                        style={{
                          width: "100%",
                          padding: "4px 6px",
                          fontSize: 12,
                          textAlign: "center",
                        }}
                      />
                    </td>
                  ))}
                  <td>
                    {isReadOnly ? (
                      <span
                        className={`badge ${row.remark === "OK" ? "badge-success" : "badge-danger"}`}
                      >
                        {row.remark || "—"}
                      </span>
                    ) : (
                      <select
                        value={row.remark}
                        onChange={(e) =>
                          updateItem(i, "remark", e.target.value)
                        }
                        style={{ fontSize: 12, padding: "4px 6px" }}
                      >
                        <option value="">—</option>
                        <option value="OK">OK</option>
                        <option value="NOT OK">NOT OK</option>
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overall remark + sign-off */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label>Remark</label>
          <textarea
            rows={3}
            disabled={isReadOnly}
            value={overallRemark}
            onChange={(e) => setOverallRemark(e.target.value)}
            placeholder="Overall inspection remark..."
          />
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}
        >
          <div className="form-group">
            <label>Checked By</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={checkedBy}
              onChange={(e) => setCheckedBy(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Approved By</label>
            <input
              type="text"
              disabled={isReadOnly}
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
            />
          </div>
        </div>
      </div>

      {!isReadOnly && (
        <button
          className="btn-primary"
          onClick={submit}
          disabled={saving}
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <Save size={15} />{" "}
          {saving ? "Saving..." : isEditRoute ? "Save Changes" : "Submit PDIR"}
        </button>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 6,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
